/**
 * LCW Drawer v1.0.2
 * Custom element que envolve um <dialog> nativo.
 *
 * Trigger em qualquer lugar da pagina:
 *   <a href="#drawer-id">Abrir</a>
 *   <button data-lcw-drawer-open="drawer-id">Abrir</button>
 *
 * Deep-link: chegar em /pagina#drawer-id abre o drawer no load.
 * Programatico: document.getElementById('drawer-id').open() / .close()
 *
 * Lazy render e feito em JS: o conteudo renderizado pelo Shopify e movido
 * pra um <template> no connectedCallback e reinserido no primeiro open().
 */

class LcwDrawer extends HTMLElement {
  constructor() {
    super();
    this._rendered = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('.lcw-drawer__dialog');
    if (!this.dialog) return;

    this.scroll = this.querySelector('[data-lcw-drawer-scroll]');
    this.inner = this.querySelector('[data-lcw-drawer-inner]');
    this.lazy = this.dataset.lazyRender === 'true';

    // Lazy render: extrai o inner pra um <template> e limpa o container
    if (this.lazy && this.inner) {
      this._lazyTemplate = document.createElement('template');
      while (this.inner.firstChild) {
        this._lazyTemplate.content.appendChild(this.inner.firstChild);
      }
    } else {
      this._rendered = true;
    }

    // Botao de fechar
    this.querySelectorAll('[data-lcw-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Sync classe do <html> no fechamento (ESC do <dialog> tambem dispara)
    this.dialog.addEventListener('close', () => this._onClose());

    // Backdrop click
    if (this.dataset.closeOnBackdrop === 'true') {
      this.dialog.addEventListener('click', (e) => this._handleBackdropClick(e));
    }
  }

  open() {
    if (!this.dialog) return;

    // Primeira abertura em modo lazy: reinsere o conteudo
    if (this.lazy && !this._rendered && this._lazyTemplate) {
      this.inner.appendChild(this._lazyTemplate.content.cloneNode(true));
      this._rendered = true;
    }

    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.classList.add('lcw-drawer-open');
      this.dispatchEvent(new CustomEvent('lcw-drawer:opened', {
        bubbles: true,
        detail: { id: this.id }
      }));
    }
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  _onClose() {
    document.documentElement.classList.remove('lcw-drawer-open');
    this.dispatchEvent(new CustomEvent('lcw-drawer:closed', {
      bubbles: true,
      detail: { id: this.id }
    }));
  }

  _handleBackdropClick(e) {
    if (e.target !== this.dialog) return;
    const rect = this.dialog.getBoundingClientRect();
    const insideDialog =
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom &&
      e.clientX >= rect.left &&
      e.clientX <= rect.right;
    if (!insideDialog) this.close();
  }
}

customElements.define('lcw-drawer', LcwDrawer);

/**
 * Delegacao global de clique.
 * Qualquer <a href="#id"> ou [data-lcw-drawer-open="id"] apontando pra
 * um <lcw-drawer> abre o drawer no lugar de rolar/navegar.
 */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('a[href^="#"], [data-lcw-drawer-open]');
  if (!trigger) return;

  let id;
  if (trigger.dataset.lcwDrawerOpen) {
    id = trigger.dataset.lcwDrawerOpen;
  } else {
    const href = trigger.getAttribute('href');
    if (!href || href === '#') return;
    id = href.slice(1);
  }

  const target = document.getElementById(id);
  if (target && target.tagName === 'LCW-DRAWER') {
    e.preventDefault();
    target.open();
  }
});

/**
 * Deep-link: se a URL carrega com #drawer-id, abre no load.
 */
function _lcwDrawerHashOpen() {
  const id = location.hash.slice(1);
  if (!id) return;
  const target = document.getElementById(id);
  if (target && target.tagName === 'LCW-DRAWER') target.open();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _lcwDrawerHashOpen);
} else {
  _lcwDrawerHashOpen();
}

window.addEventListener('hashchange', _lcwDrawerHashOpen);